/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { db, functions } from "../../firebase";
import { httpsCallable } from "firebase/functions";
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  getDoc,
  serverTimestamp,
  doc,
  writeBatch,
  arrayUnion,
  arrayRemove,
  setDoc,
  Timestamp,
  query,
  where,
} from "firebase/firestore";
import {
  FadeUpStagger,
  MotionCard,
  MotionButton,
} from "../motion/AdminMotion";

const INVITE_EXPIRY_DAYS = 7;

function generateToken() {
  return `${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
}

function buildInviteLink(token) {
  const base = String(window.location.origin || "https://presentplus.uk").replace(/\/$/, "");
  return `${base}/accept-parent-invite?token=${encodeURIComponent(token)}`;
}

function buildInviteExpiryDate() {
  return Timestamp.fromDate(
    new Date(Date.now() + INVITE_EXPIRY_DAYS * 24 * 60 * 60 * 1000)
  );
}

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

function cleanText(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function displayName(student) {
  const combined = [student?.firstName, student?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    student?.name ||
    student?.fullName ||
    student?.displayName ||
    student?.studentName ||
    student?.childName ||
    combined ||
    "Unnamed student"
  );
}

function normalizeNameList(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return cleanText(item);
        return cleanText(
          item?.name ||
            item?.fullName ||
            item?.displayName ||
            item?.studentName ||
            item?.childName ||
            ""
        );
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => cleanText(item))
      .filter(Boolean);
  }

  return [];
}

function normalizeIdList(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => cleanText(item))
      .filter(Boolean);
  }
  return [];
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}


function toDateValue(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatActivityDate(value) {
  const date = toDateValue(value);
  if (!date) return "—";

  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  return new Intl.DateTimeFormat("en-GB", {
    day: sameDay ? undefined : "2-digit",
    month: sameDay ? undefined : "short",
    year: sameDay ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getEngagementTone(status) {
  const value = String(status || "").toLowerCase();

  if (value === "active") {
    return {
      color: "#027a48",
      background: "#dcfae6",
      border: "1px solid #abefc6",
    };
  }

  if (value === "joined") {
    return {
      color: "#175cd3",
      background: "#dbeafe",
      border: "1px solid #bfdcff",
    };
  }

  if (value === "invited") {
    return {
      color: "#b54708",
      background: "#fef0c7",
      border: "1px solid #fedf89",
    };
  }

  if (value === "inactive") {
    return {
      color: "#b42318",
      background: "#fee4e2",
      border: "1px solid #fecdca",
    };
  }

  return {
    color: "#475467",
    background: "#f2f4f7",
    border: "1px solid #e4e7ec",
  };
}

function engagementLabel(status) {
  const labels = {
    not_joined: "Not joined",
    invited: "Invited",
    joined: "Joined",
    active: "Active",
    inactive: "Inactive",
  };

  return labels[status] || "Not joined";
}

function getStudentMeta(student, classMap = {}) {
  const classDoc = student?.classId ? classMap[student.classId] : null;

  return (
    classDoc?.name ||
    classDoc?.className ||
    classDoc?.yearGroup ||
    student?.className ||
    student?.yearGroup ||
    student?.year ||
    student?.admissionNumber ||
    student?.admissionNo ||
    "No class assigned"
  );
}

function getStatusTone(status) {
  const value = String(status || "").toLowerCase();

  if (value === "active" || value === "approved" || value === "ready") {
    return {
      color: "#027a48",
      background: "#dcfae6",
      border: "1px solid #abefc6",
    };
  }

  if (value === "accepted" || value === "sent") {
    return {
      color: "#175cd3",
      background: "#dbeafe",
      border: "1px solid #bfdcff",
    };
  }

  if (value === "needs link" || value === "draft") {
    return {
      color: "#b54708",
      background: "#fef0c7",
      border: "1px solid #fedf89",
    };
  }

  if (value === "rejected" || value === "revoked" || value === "expired") {
    return {
      color: "#b42318",
      background: "#fee4e2",
      border: "1px solid #fecdca",
    };
  }

  return {
    color: "#344054",
    background: "#f2f4f7",
    border: "1px solid #e4e7ec",
  };
}

function splitCsvLine(line) {
  const output = [];
  let current = "";
  let insideQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && nextChar === '"') {
      current += '"';
      i += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === "," && !insideQuotes) {
      output.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  output.push(current.trim());
  return output;
}

const RELATIONSHIP_OPTIONS = [
  { value: "mother", label: "Mother" },
  { value: "father", label: "Father" },
  { value: "guardian", label: "Guardian" },
  { value: "foster_carer", label: "Foster Carer" },
  { value: "grandparent", label: "Grandparent" },
  { value: "step_parent", label: "Step-parent" },
  { value: "other", label: "Other" },
];

function getRelationshipLabel(value, otherValue = "") {
  const key = String(value || "").trim().toLowerCase();
  if (key === "other") return cleanText(otherValue) || "Other";
  return (
    RELATIONSHIP_OPTIONS.find((item) => item.value === key)?.label ||
    "Guardian"
  );
}

function normaliseRelationship(value) {
  const key = String(value || "").trim().toLowerCase();
  return RELATIONSHIP_OPTIONS.some((item) => item.value === key)
    ? key
    : "guardian";
}

function relationshipForStudent(source, studentId) {
  const rows = Array.isArray(source?.studentRelationships)
    ? source.studentRelationships
    : [];

  const match = rows.find(
    (item) => String(item?.studentId || "") === String(studentId || "")
  );

  const fallbackValue = normaliseRelationship(
    match?.relationship || source?.relationship || "guardian"
  );

  return {
    relationship: fallbackValue,
    relationshipLabel:
      cleanText(match?.relationshipLabel) ||
      cleanText(source?.relationshipLabel) ||
      getRelationshipLabel(fallbackValue),
  };
}

function relationshipSummary(source, studentNameMap = {}) {
  const ids = normalizeIdList(source?.linkedStudentIds);
  if (!ids.length) return [];

  return ids.map((studentId) => {
    const relationship = relationshipForStudent(source, studentId);
    return {
      studentId,
      studentName: studentNameMap[studentId] || "Student",
      ...relationship,
    };
  });
}

export default function ParentsSection({ schoolId }) {
  const currentSchoolId = schoolId || "";

  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [parentInvites, setParentInvites] = useState([]);
  const [parentDrafts, setParentDrafts] = useState([]);
  const [schoolInfo, setSchoolInfo] = useState(null);
  const [parentActivity, setParentActivity] = useState([]);

  const [studentSearch, setStudentSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [draftFilter, setDraftFilter] = useState("all"); // 🔥 NEW

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    relationship: "guardian",
    relationshipOther: "",
    linkedStudentIds: [],
  });

  const [editingParent, setEditingParent] = useState(null);
  const [editStudentIds, setEditStudentIds] = useState([]);
  const [editRelationships, setEditRelationships] = useState({});
  const [editSearch, setEditSearch] = useState("");

  const loadData = async () => {
    try {
      const [parentsSnap, studentsSnap, classesSnap, invitesSnap, draftsSnap, schoolSnap] =
        await Promise.all([
          getDocs(collection(db, "schools", currentSchoolId, "parents")),
          getDocs(collection(db, "schools", currentSchoolId, "students")),
          getDocs(collection(db, "schools", currentSchoolId, "classes")),
          getDocs(collection(db, "schools", currentSchoolId, "invites")),
          getDocs(collection(db, "schools", currentSchoolId, "parentDrafts")),
          getDoc(doc(db, "schools", currentSchoolId, "settings", "school")),
        ]);

      const parentList = parentsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => !p.deletedAt)
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );

      const studentList = studentsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((s) => !s.deletedAt)
        .sort((a, b) => displayName(a).localeCompare(displayName(b)));

        const classList = classesSnap.docs
  .map((d) => ({ id: d.id, ...d.data() }))
  .filter((c) => !c.deletedAt);

setClasses(classList);

      const inviteList = invitesSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter(
          (x) =>
            String(x.role || "").toLowerCase() === "parent" && !x.deletedAt
        );

      // 🔥 STEP 3: DRAFTS (hide already sent)
      const draftList = draftsSnap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => !d.deletedAt)
        .filter((d) => String(d.status || "").toLowerCase() !== "sent")
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""))
        );


      let activityList = [];

      try {
        const activitySnap = await getDocs(
          query(
            collection(db, "users"),
            where("schoolId", "==", currentSchoolId)
          )
        );

        activityList = activitySnap.docs
          .map((activityDoc) => ({
            id: activityDoc.id,
            ...activityDoc.data(),
          }))
          .filter(
            (record) =>
              String(record.role || "").toLowerCase() === "parent" &&
              !record.deletedAt
          );
      } catch (activityError) {
        console.warn("Parent engagement data could not be loaded:", activityError);
      }

      setParents(parentList);
      setStudents(studentList);
      setParentInvites(inviteList);
      setParentDrafts(draftList);
      setParentActivity(activityList);
      setSchoolInfo(schoolSnap.exists() ? schoolSnap.data() : null);
      setError("");
    } catch (err) {
      console.error("loadData error:", err);
      setError("Failed to load parent data.");
    }
  };

  useEffect(() => {
    if (!currentSchoolId) return;
    loadData();
  }, [currentSchoolId]);

    const inviteMapByParentId = useMemo(() => {
    const map = {};

    parentInvites.forEach((invite) => {
      if (invite.parentId) {
        map[invite.parentId] = invite;
      }
    });

    return map;
  }, [parentInvites]);


  const activityByUid = useMemo(() => {
    const map = {};

    parentActivity.forEach((record) => {
      const uid = String(record.uid || record.id || "").trim();
      if (uid) map[uid] = record;
    });

    return map;
  }, [parentActivity]);

  const activityByEmail = useMemo(() => {
    const map = {};

    parentActivity.forEach((record) => {
      const email = normalizeEmail(record.email);
      if (email) map[email] = record;
    });

    return map;
  }, [parentActivity]);

  const resolveParentActivity = (parent) => {
    const uid = String(
      parent?.uid ||
      parent?.userId ||
      parent?.authUid ||
      parent?.parentUid ||
      ""
    ).trim();

    if (uid && activityByUid[uid]) return activityByUid[uid];

    const email = normalizeEmail(parent?.email);
    if (email && activityByEmail[email]) return activityByEmail[email];

    return null;
  };

  const getEngagementStatus = (parent) => {
    const activity = resolveParentActivity(parent);
    const invite = inviteMapByParentId[parent.id] || null;

    const inviteSentAt =
      parent?.inviteSentAt ||
      invite?.sentAt ||
      invite?.emailSentAt ||
      invite?.createdAt ||
      null;

    const accountCreatedAt =
      activity?.accountCreatedAt ||
      activity?.createdAt ||
      parent?.accountCreatedAt ||
      parent?.joinedAt ||
      invite?.acceptedAt ||
      null;

    const firstLoginAt =
      activity?.firstLoginAt ||
      parent?.firstLoginAt ||
      null;

    const lastActiveAt =
      activity?.lastActiveAt ||
      activity?.lastLoginAt ||
      parent?.lastActiveAt ||
      parent?.lastLoginAt ||
      null;

    if (firstLoginAt) {
      const lastActiveDate = toDateValue(lastActiveAt || firstLoginAt);
      const inactiveAfter = 30 * 24 * 60 * 60 * 1000;

      if (
        lastActiveDate &&
        Date.now() - lastActiveDate.getTime() > inactiveAfter
      ) {
        return "inactive";
      }

      return "active";
    }

    if (accountCreatedAt) return "joined";
    if (inviteSentAt) return "invited";
    return "not_joined";
  };

  const studentNameMap = useMemo(() => {
    const map = {};

    students.forEach((s) => {
      map[s.id] = displayName(s);
    });

    return map;
  }, [students]);

  const classMap = useMemo(() => {
  const map = {};
  classes.forEach((c) => {
    map[c.id] = c;
  });
  return map;
}, [classes]);

  const existingParentEmails = useMemo(() => {
    return new Set(parents.map((p) => normalizeEmail(p.email)));
  }, [parents]);

  const existingInviteEmails = useMemo(() => {
    return new Set(parentInvites.map((i) => normalizeEmail(i.email)));
  }, [parentInvites]);

  const existingDraftEmails = useMemo(() => {
    return new Set(parentDrafts.map((d) => normalizeEmail(d.email)));
  }, [parentDrafts]);

  const filteredStudents = useMemo(() => {
    const q = studentSearch.trim().toLowerCase();

    return students.filter((s) => {
      if (form.linkedStudentIds.includes(s.id)) return false;
      if (!q) return true;

      const name = String(displayName(s)).toLowerCase();
      const classId = String(s.classId || s.className || "").toLowerCase();
      const admissionNumber = String(
        s.admissionNumber || s.admissionNo || ""
      ).toLowerCase();

      return (
        name.includes(q) ||
        classId.includes(q) ||
        admissionNumber.includes(q)
      );
    });
  }, [students, studentSearch, form.linkedStudentIds]);

  const editableStudents = useMemo(() => {
    const q = editSearch.trim().toLowerCase();

    return students.filter((s) => {
      if (!q) return true;

      const name = String(displayName(s)).toLowerCase();
      const classId = String(s.classId || s.className || "").toLowerCase();
      const admissionNumber = String(
        s.admissionNumber || s.admissionNo || ""
      ).toLowerCase();

      return (
        name.includes(q) ||
        classId.includes(q) ||
        admissionNumber.includes(q)
      );
    });
  }, [students, editSearch]);

  const getDisplayStatus = (parent) => {
    const parentStatus = String(parent.status || "").toLowerCase();
    const invite = inviteMapByParentId[parent.id];
    const inviteStatus = String(invite?.status || "").toLowerCase();

    if (inviteStatus === "approved" || parentStatus === "active") return "active";
    if (inviteStatus === "accepted") return "accepted";
    if (inviteStatus === "revoked") return "revoked";
    if (inviteStatus === "expired") return "expired";
    if (inviteStatus === "rejected" || parentStatus === "rejected") return "rejected";

    return "pending";
  };

  const parentRows = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();

    let rows = parents.map((parent) => {
      const status = getDisplayStatus(parent);
      const activity = resolveParentActivity(parent);
      const engagementStatus = getEngagementStatus(parent);

      return {
        ...parent,
        displayStatus: status,
        engagementStatus,
        firstLoginAt: activity?.firstLoginAt || parent?.firstLoginAt || null,
        lastActiveAt:
          activity?.lastActiveAt ||
          activity?.lastLoginAt ||
          parent?.lastActiveAt ||
          parent?.lastLoginAt ||
          null,
        loginCount: Number(activity?.loginCount || parent?.loginCount || 0),
      };
    });

    if (statusFilter !== "all") {
      rows = rows.filter(
        (row) =>
          row.displayStatus === statusFilter ||
          row.engagementStatus === statusFilter
      );
    }

    if (!q) return rows;

    return rows.filter((row) => {
      const hay = [
        row.name,
        row.email,
        row.relationship,
        row.relationshipLabel,
        row.displayStatus,
        row.engagementStatus,
        engagementLabel(row.engagementStatus),
        ...resolveLinkedStudentNames(row),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [parents, tableSearch, studentNameMap, inviteMapByParentId, statusFilter, parentActivity]);

  const draftRows = useMemo(() => {
    let rows = parentDrafts.map((draft) => {
      const linkedIds = Array.isArray(draft.linkedStudentIds)
        ? draft.linkedStudentIds
        : [];

      return {
        ...draft,
        linkedIds,
        linkedNames: normalizeNameList(draft.linkedStudentNames),
        isReady: linkedIds.length > 0,
      };
    });

    if (draftFilter === "ready") {
      rows = rows.filter((draft) => draft.isReady);
    }

    if (draftFilter === "needsLink") {
      rows = rows.filter((draft) => !draft.isReady);
    }

    return rows;
  }, [parentDrafts, draftFilter]);

  const readyDrafts = useMemo(() => {
    return parentDrafts.filter(
      (draft) =>
        Array.isArray(draft.linkedStudentIds) && draft.linkedStudentIds.length
    );
  }, [parentDrafts]);

  const summary = useMemo(() => {
    let active = 0;
    let pending = 0;
    let accepted = 0;
    let rejected = 0;
    let invited = 0;
    let joined = 0;
    let firstLogin = 0;
    let engaged = 0;
    let inactive = 0;

    parents.forEach((parent) => {
      const status = getDisplayStatus(parent);
      const engagementStatus = getEngagementStatus(parent);

      if (status === "active") active += 1;
      else if (status === "accepted") accepted += 1;
      else if (
        status === "rejected" ||
        status === "revoked" ||
        status === "expired"
      ) {
        rejected += 1;
      } else {
        pending += 1;
      }

      if (engagementStatus === "invited") invited += 1;
      if (["joined", "active", "inactive"].includes(engagementStatus)) joined += 1;
      if (["active", "inactive"].includes(engagementStatus)) firstLogin += 1;
      if (engagementStatus === "active") engaged += 1;
      if (engagementStatus === "inactive") inactive += 1;
    });

    const ready = parentDrafts.filter(
      (draft) =>
        Array.isArray(draft.linkedStudentIds) &&
        draft.linkedStudentIds.length > 0
    ).length;

    const needsLink = parentDrafts.length - ready;

    return {
      total: parents.length,
      active,
      pending,
      accepted,
      rejected,
      invited,
      joined,
      firstLogin,
      engaged,
      inactive,
      drafts: parentDrafts.length,
      readyDrafts: ready,
      needsLinkDrafts: needsLink,
    };
  }, [parents, parentDrafts, inviteMapByParentId, parentActivity]);

  const handleSelectStudent = (studentId) => {
    setForm((prev) => ({
      ...prev,
      linkedStudentIds: [...prev.linkedStudentIds, studentId],
    }));

    setStudentSearch("");
    setDropdownOpen(false);
  };

  const handleRemoveStudent = (studentId) => {
    setForm((prev) => ({
      ...prev,
      linkedStudentIds: prev.linkedStudentIds.filter((id) => id !== studentId),
    }));
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      relationship: "guardian",
      relationshipOther: "",
      linkedStudentIds: [],
    });

    setStudentSearch("");
    setDropdownOpen(false);
  };

  const openManageChildren = (item) => {
    const linkedIds = Array.isArray(item.linkedStudentIds)
      ? item.linkedStudentIds
      : [];

    const relationshipMap = {};
    linkedIds.forEach((studentId) => {
      const rel = relationshipForStudent(item, studentId);
      relationshipMap[studentId] = rel.relationship;
    });

    setEditingParent(item);
    setEditStudentIds(linkedIds);
    setEditRelationships(relationshipMap);
    setEditSearch("");
    setError("");
    setSuccess("");
  };

  const closeManageChildren = () => {
    setEditingParent(null);
    setEditStudentIds([]);
    setEditRelationships({});
    setEditSearch("");
  };

  const toggleEditStudent = (studentId) => {
    setEditStudentIds((prev) => {
      if (prev.includes(studentId)) {
        setEditRelationships((current) => {
          const next = { ...current };
          delete next[studentId];
          return next;
        });
        return prev.filter((id) => id !== studentId);
      }

      setEditRelationships((current) => ({
        ...current,
        [studentId]:
          current[studentId] ||
          relationshipForStudent(editingParent, studentId).relationship ||
          "guardian",
      }));

      return [...prev, studentId];
    });
  };

  const changeEditRelationship = (studentId, relationship) => {
    setEditRelationships((current) => ({
      ...current,
      [studentId]: normaliseRelationship(relationship),
    }));
  };
    const saveLinkedChildren = async () => {
    if (!editingParent) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const oldStudentIds = Array.isArray(editingParent.linkedStudentIds)
        ? editingParent.linkedStudentIds
        : [];

      const validStudentIds = editStudentIds.filter((id) =>
        students.some((student) => student.id === id)
      );

      const studentRelationships = validStudentIds.map((studentId) => {
        const relationship = normaliseRelationship(
          editRelationships[studentId] ||
            relationshipForStudent(editingParent, studentId).relationship ||
            "guardian"
        );

        return {
          studentId,
          relationship,
          relationshipLabel: getRelationshipLabel(relationship),
        };
      });

      const primaryRelationship =
        studentRelationships[0]?.relationship ||
        normaliseRelationship(editingParent?.relationship || "guardian");

      const primaryRelationshipLabel =
        studentRelationships[0]?.relationshipLabel ||
        getRelationshipLabel(primaryRelationship);

      const isDraft = editingParent.__type === "draft";

      if (isDraft) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "parentDrafts", editingParent.id),
          {
            linkedStudentIds: validStudentIds,
            linkedStudentNames: validStudentIds.map(
              (id) => studentNameMap[id] || id
            ),
            studentRelationships,
            relationship: primaryRelationship,
            relationshipLabel: primaryRelationshipLabel,
            status: validStudentIds.length ? "ready" : "draft",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setSuccess("Draft linked children updated successfully.");
        closeManageChildren();
        await loadData();
        return;
      }

      await setDoc(
        doc(db, "schools", currentSchoolId, "parents", editingParent.id),
        {
          linkedStudentIds: validStudentIds,
          linkedStudentNames: validStudentIds.map(
            (id) => studentNameMap[id] || id
          ),
          studentRelationships,
          relationship: primaryRelationship,
          relationshipLabel: primaryRelationshipLabel,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const invite = inviteMapByParentId[editingParent.id];

      if (invite?.id) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", invite.id),
          {
            linkedStudentIds: validStudentIds,
            linkedStudentNames: validStudentIds.map(
              (id) => studentNameMap[id] || id
            ),
            studentRelationships,
            relationship: primaryRelationship,
            relationshipLabel: primaryRelationshipLabel,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      const batch = writeBatch(db);

      oldStudentIds.forEach((studentId) => {
        if (!validStudentIds.includes(studentId)) {
          batch.set(
            doc(db, "schools", currentSchoolId, "students", studentId),
            {
              parentIds: arrayRemove(editingParent.id),
              linkedParentIds: arrayRemove(editingParent.id),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
        }
      });

      validStudentIds.forEach((studentId) => {
        batch.set(
          doc(db, "schools", currentSchoolId, "students", studentId),
          {
            parentIds: arrayUnion(editingParent.id),
            linkedParentIds: arrayUnion(editingParent.id),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      await batch.commit();

      setSuccess("Linked children updated successfully.");
      closeManageChildren();
      await loadData();
    } catch (err) {
      console.error("save linked children error:", err);
      setError(err?.message || "Failed to update linked children.");
    } finally {
      setSaving(false);
    }
  };

  const sendParentInvite = async ({ email, name, inviteLink, schoolName }) => {
    const callable = httpsCallable(functions, "sendParentInvite");

    return callable({
      schoolId: currentSchoolId,
      email,
      name,
      inviteLink,
      schoolName: schoolName || schoolInfo?.name || "PresentPlus School",
    });
  };
    const handleCSVUpload = async (file) => {
    setError("");
    setSuccess("");

    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split("\n").filter((l) => l.trim());
      const headers = splitCsvLine(lines[0]).map((h) =>
  String(h || "").trim().toLowerCase()
);

const hasBaseHeaders =
  headers.length >= 2 &&
  headers[0] === "parent name" &&
  headers[1] === "parent email";

const hasValidRelationshipHeader =
  headers.length === 2 ||
  (headers.length === 3 && headers[2] === "relationship");

if (!hasBaseHeaders || !hasValidRelationshipHeader) {
  setError(
    "Invalid CSV format. Use: Parent Name, Parent Email, Relationship (optional)"
  );
  return;
}

      if (lines.length <= 1) {
        setError("CSV is empty or invalid.");
        return;
      }

      const parsed = [];
      const duplicates = [];
      const seenEmails = new Set();

      lines.slice(1).forEach((line, index) => {
        const [nameRaw, emailRaw, relationshipRaw] = splitCsvLine(line);

        const name = cleanText(nameRaw);
        const email = normalizeEmail(emailRaw);
        const rawRelationship = cleanText(relationshipRaw);
        const matchedRelationship = RELATIONSHIP_OPTIONS.find(
          (item) =>
            item.value === rawRelationship.toLowerCase().replace(/\s+/g, "_") ||
            item.label.toLowerCase() === rawRelationship.toLowerCase()
        );
        const relationship = matchedRelationship?.value || "guardian";
        const relationshipLabel =
          rawRelationship && !matchedRelationship
            ? rawRelationship
            : getRelationshipLabel(relationship);

        if (!name || !email) return;

        if (
          seenEmails.has(email) ||
          existingParentEmails.has(email) ||
          existingInviteEmails.has(email) ||
          existingDraftEmails.has(email)
        ) {
          duplicates.push({ name, email, row: index + 2 });
          return;
        }

        seenEmails.add(email);

        parsed.push({
          name,
          email,
          relationship,
          relationshipLabel,
        });
      });

      if (!parsed.length) {
        setError("All rows are duplicates or invalid.");
        return;
      }

      const batch = writeBatch(db);

      parsed.forEach((parent) => {
        const ref = doc(
          collection(db, "schools", currentSchoolId, "parentDrafts")
        );

        batch.set(ref, {
          ...parent,
          schoolId: currentSchoolId,
          linkedStudentIds: [],
          linkedStudentNames: [],
          status: "draft",
          createdAt: serverTimestamp(),
        });
      });

      await batch.commit();

      if (duplicates.length) {
        setError(
          `${duplicates.length} duplicate(s) skipped. Cleaned ${parsed.length} added.`
        );
      } else {
        setSuccess(`${parsed.length} parents uploaded successfully.`);
      }

      await loadData();
    } catch (err) {
      console.error(err);
      setError("CSV upload failed.");
    }
  };

  const handleBulkSendInvites = async () => {
    const ready = parentDrafts.filter(
      (d) =>
        Array.isArray(d.linkedStudentIds) &&
        d.linkedStudentIds.length > 0
    );

    if (!ready.length) {
      setError("No ready drafts to send.");
      return;
    }

    if (
      !window.confirm(
        `Send ${ready.length} invites? This cannot be undone.`
      )
    ) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      for (const draft of ready) {
        const inviteToken = generateToken();
        const inviteLink = buildInviteLink(inviteToken);
        const inviteExpiresAt = buildInviteExpiryDate();

        const parentRef = await addDoc(
          collection(db, "schools", currentSchoolId, "parents"),
          {
            name: draft.name,
            email: draft.email,
            relationship: normaliseRelationship(draft.relationship),
            relationshipLabel:
              draft.relationshipLabel ||
              getRelationshipLabel(draft.relationship),
            linkedStudentIds: draft.linkedStudentIds,
            studentRelationships: draft.linkedStudentIds.map((studentId) => ({
              studentId,
              relationship: normaliseRelationship(draft.relationship),
              relationshipLabel:
                draft.relationshipLabel ||
                getRelationshipLabel(draft.relationship),
            })),
            schoolId: currentSchoolId,
            status: "pending",
            inviteToken,
            inviteExpiresAt,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          }
        );

        const inviteRef = await addDoc(
          collection(db, "schools", currentSchoolId, "invites"),
          {
            parentId: parentRef.id,
            name: draft.name,
            email: draft.email,
            relationship: normaliseRelationship(draft.relationship),
            relationshipLabel:
              draft.relationshipLabel ||
              getRelationshipLabel(draft.relationship),
            linkedStudentIds: draft.linkedStudentIds,
            studentRelationships: draft.linkedStudentIds.map((studentId) => ({
              studentId,
              relationship: normaliseRelationship(draft.relationship),
              relationshipLabel:
                draft.relationshipLabel ||
                getRelationshipLabel(draft.relationship),
            })),
            schoolId: currentSchoolId,
            role: "parent",
            status: "pending",
            inviteToken,
            inviteLink,
            inviteExpiresAt,
            createdAt: serverTimestamp(),
          }
        );

        await sendParentInvite({
          email: draft.email,
          name: draft.name,
          inviteLink,
        });

        await setDoc(
          doc(db, "schools", currentSchoolId, "parentDrafts", draft.id),
          {
            status: "sent",
            parentId: parentRef.id,
            inviteId: inviteRef.id,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      setSuccess("Bulk invites sent successfully.");
      await loadData();
    } catch (err) {
      console.error(err);
      setError("Bulk send failed.");
    } finally {
      setSaving(false);
    }
  };
    const handleInviteParent = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const cleanName = cleanText(form.name);
    const cleanEmail = normalizeEmail(form.email);
    const relationship = normaliseRelationship(form.relationship);
    const relationshipLabel = getRelationshipLabel(
      relationship,
      form.relationshipOther
    );

    const validLinkedStudentIds = form.linkedStudentIds.filter((id) =>
      students.some((student) => student.id === id)
    );

    if (!cleanName) {
      setError("Parent name is required.");
      return;
    }

    if (!cleanEmail) {
      setError("Parent email is required.");
      return;
    }

    if (relationship === "other" && !cleanText(form.relationshipOther)) {
      setError("Please enter the parent relationship.");
      return;
    }

    if (!validLinkedStudentIds.length) {
      setError("Please link at least one valid student.");
      return;
    }

    if (existingParentEmails.has(cleanEmail)) {
      setError("A parent with this email already exists.");
      return;
    }

    if (existingInviteEmails.has(cleanEmail)) {
      setError("A parent invite for this email already exists.");
      return;
    }

    if (existingDraftEmails.has(cleanEmail)) {
      setError("This email already exists as a parent draft.");
      return;
    }

    try {
      setSaving(true);

      const inviteToken = generateToken();
      const inviteLink = buildInviteLink(inviteToken);
      const inviteExpiresAt = buildInviteExpiryDate();

      const linkedStudentNames = validLinkedStudentIds.map(
        (id) => studentNameMap[id] || id
      );

      const studentRelationships = validLinkedStudentIds.map((studentId) => ({
        studentId,
        relationship,
        relationshipLabel,
      }));

      /*
        Production-safe invite transaction:
        Create parent + invite + student links together.
        If Firestore rules reject any student/settings update, no half-created
        parent record is left behind before the invite email is attempted.
      */
      const parentRef = doc(collection(db, "schools", currentSchoolId, "parents"));
      const inviteRef = doc(collection(db, "schools", currentSchoolId, "invites"));
      const batch = writeBatch(db);

      batch.set(parentRef, {
        name: cleanName,
        email: cleanEmail,
        relationship,
        relationshipLabel,
        studentRelationships,
        linkedStudentIds: validLinkedStudentIds,
        linkedStudentNames,
        schoolId: currentSchoolId,
        status: "pending",
        inviteToken,
        inviteStatus: "pending",
        inviteExpiresAt,
        inviteSentAt: serverTimestamp(),
        inviteId: inviteRef.id,
        source: "manual",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      batch.set(inviteRef, {
        parentId: parentRef.id,
        name: cleanName,
        email: cleanEmail,
        relationship,
        relationshipLabel,
        studentRelationships,
        linkedStudentIds: validLinkedStudentIds,
        linkedStudentNames,
        schoolId: currentSchoolId,
        role: "parent",
        status: "pending",
        inviteToken,
        inviteLink,
        inviteExpiresAt,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        sentAt: serverTimestamp(),
        acceptedAt: null,
        approvedAt: null,
        rejectedAt: null,
        revokedAt: null,
        expiredAt: null,
        emailStatus: "pending",
      });

      validLinkedStudentIds.forEach((studentId) => {
        batch.set(
          doc(db, "schools", currentSchoolId, "students", studentId),
          {
            parentIds: arrayUnion(parentRef.id),
            linkedParentIds: arrayUnion(parentRef.id),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      });

      batch.set(
        doc(db, "schools", currentSchoolId, "settings", "school"),
        {
          parentsReady: true,
          onboardingStep: 6,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await batch.commit();

      try {
        await sendParentInvite({
          email: cleanEmail,
          name: cleanName,
          inviteLink,
          schoolName: schoolInfo?.name || "PresentPlus School",
        });

        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", inviteRef.id),
          {
            emailStatus: "sent",
            emailSentAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setSuccess("Parent invite created and email sent successfully.");
      } catch (mailErr) {
        console.error("sendParentInvite error:", mailErr);

        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", inviteRef.id),
          {
            emailStatus: "failed",
            emailError: mailErr?.message || "Email send failed",
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );

        setSuccess("Parent record created, but the email failed to send.");
        setError(mailErr?.message || "Email failed to send. Use Resend after checking email settings.");
      }

      resetForm();
      await loadData();
    } catch (err) {
      console.error("handleInviteParent error:", err);
      setError(err?.message || "Failed to create parent invite.");
    } finally {
      setSaving(false);
    }
  };
    const handleApprove = async (parent) => {
    try {
      setBusyId(parent.id);
      setError("");
      setSuccess("");

      await setDoc(
        doc(db, "schools", currentSchoolId, "parents", parent.id),
        {
          status: "active",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const invite = inviteMapByParentId[parent.id];

      if (invite?.id) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", invite.id),
          {
            status: "approved",
            approvedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await loadData();
      setSuccess("Parent approved successfully.");
    } catch (err) {
      console.error("approve parent error:", err);
      setError(err?.message || "Failed to approve parent.");
    } finally {
      setBusyId("");
    }
  };

  const handleReject = async (parent) => {
    try {
      setBusyId(parent.id);
      setError("");
      setSuccess("");

      await setDoc(
        doc(db, "schools", currentSchoolId, "parents", parent.id),
        {
          status: "rejected",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      const invite = inviteMapByParentId[parent.id];

      if (invite?.id) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", invite.id),
          {
            status: "rejected",
            rejectedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await loadData();
      setSuccess("Parent invite rejected.");
    } catch (err) {
      console.error("reject parent error:", err);
      setError(err?.message || "Failed to reject parent.");
    } finally {
      setBusyId("");
    }
  };

  const handleResendInvite = async (parent) => {
    const invite = inviteMapByParentId[parent.id];

    if (!invite?.id) {
      setError("Invite record not found.");
      return;
    }

    const status = String(invite.status || "").toLowerCase();

    if (["approved", "accepted"].includes(status)) {
      setError("This invite can no longer be resent.");
      return;
    }

    setBusyId(parent.id);
    setError("");
    setSuccess("");

    try {
      const newToken = generateToken();
      const inviteLink = buildInviteLink(newToken);
      const inviteExpiresAt = buildInviteExpiryDate();

      await setDoc(
        doc(db, "schools", currentSchoolId, "invites", invite.id),
        {
          status: "pending",
          inviteToken: newToken,
          inviteLink,
          inviteExpiresAt,
          sentAt: serverTimestamp(),
          revokedAt: null,
          rejectedAt: null,
          expiredAt: null,
          emailStatus: "pending",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await setDoc(
        doc(db, "schools", currentSchoolId, "parents", parent.id),
        {
          status: "pending",
          inviteStatus: "pending",
          inviteToken: newToken,
          inviteExpiresAt,
          inviteSentAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await sendParentInvite({
        email: parent.email,
        name: parent.name,
        inviteLink,
        schoolName: schoolInfo?.name || "PresentPlus School",
      });

      await setDoc(
        doc(db, "schools", currentSchoolId, "invites", invite.id),
        {
          emailStatus: "sent",
          emailSentAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await loadData();
      setSuccess("Invite resent successfully.");
    } catch (err) {
      console.error("resend invite error:", err);

      await setDoc(
        doc(db, "schools", currentSchoolId, "invites", invite.id),
        {
          emailStatus: "failed",
          emailError: err?.message || "Email resend failed",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setError(err?.message || "Failed to resend invite.");
    } finally {
      setBusyId("");
    }
  };
    const handleRevokeInvite = async (parent) => {
    try {
      setBusyId(parent.id);
      setError("");
      setSuccess("");

      const invite = inviteMapByParentId[parent.id];

      if (invite?.id) {
        await setDoc(
          doc(db, "schools", currentSchoolId, "invites", invite.id),
          {
            status: "revoked",
            revokedAt: serverTimestamp(),
            inviteToken: null,
            inviteLink: null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await setDoc(
        doc(db, "schools", currentSchoolId, "parents", parent.id),
        {
          status: "rejected",
          inviteStatus: "revoked",
          inviteToken: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await loadData();
      setSuccess("Invite revoked successfully.");
    } catch (err) {
      console.error("revoke parent error:", err);
      setError(err?.message || "Failed to revoke invite.");
    } finally {
      setBusyId("");
    }
  };

  const handleDelete = async (parent) => {
    const confirmed = window.confirm(
      `Remove ${parent.name || "this parent"} from the parent directory?`
    );

    if (!confirmed) return;

    try {
      setBusyId(parent.id);
      setError("");
      setSuccess("");

      const invite = inviteMapByParentId[parent.id];

      /*
        Safe delete strategy:
        Do not hard-delete the parent, invite, or student records from the client.
        The previous version tried to:
          - delete the invite document
          - update every linked student document
          - delete the parent document
        in one batch. If any linked student ID was stale or not a real student
        document ID, Firestore treated the write as a create and blocked it.

        This soft-delete pattern is safer for schools:
          - the parent disappears from the directory because loadData filters deletedAt
          - the invite is disabled and hidden
          - no risky student writes are made from the browser
          - audit-friendly fields remain on the records
      */

      const batch = writeBatch(db);

      batch.set(
        doc(db, "schools", currentSchoolId, "parents", parent.id),
        {
          status: "deleted",
          inviteStatus: "deleted",
          deletedAt: serverTimestamp(),
          deletedBy: "admin",
          inviteToken: null,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      if (invite?.id) {
        batch.set(
          doc(db, "schools", currentSchoolId, "invites", invite.id),
          {
            status: "deleted",
            deletedAt: serverTimestamp(),
            deletedBy: "admin",
            inviteToken: null,
            inviteLink: null,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }

      await batch.commit();

      await loadData();
      setSuccess("Parent removed from the directory.");
    } catch (err) {
      console.error("delete parent error:", err);
      setError(err?.message || "Failed to remove parent.");
    } finally {
      setBusyId("");
    }
  };

  const handleDeleteDraft = async (draft) => {
    const confirmed = window.confirm(
      `Delete draft for ${draft.name || draft.email || "this parent"}?`
    );

    if (!confirmed) return;

    try {
      setBusyId(`draft-${draft.id}`);
      setError("");
      setSuccess("");

      await deleteDoc(
        doc(db, "schools", currentSchoolId, "parentDrafts", draft.id)
      );

      await loadData();
      setSuccess("Parent draft deleted.");
    } catch (err) {
      console.error("delete draft error:", err);
      setError(err?.message || "Failed to delete draft.");
    } finally {
      setBusyId("");
    }
  };

  const looksLikeFirestoreId = (value) => {
    const text = String(value || "").trim();
    return /^[A-Za-z0-9_-]{16,}$/.test(text) && !/\s/.test(text);
  };

  const resolveLinkedStudentNames = (source, explicitIds = null) => {
    const storedNames = normalizeNameList(source?.linkedStudentNames || source?.linkedStudentNamesText);
    if (storedNames.length) return storedNames;

    const ids = explicitIds || normalizeIdList(source?.linkedStudentIds || source?.linkedIds || source);

    return ids.map((id) => {
      const name = studentNameMap[id];
      if (name) return name;

      const matchedStudent = students.find((student) => {
        return (
          String(student.id) === String(id) ||
          String(student.admissionNumber || "") === String(id) ||
          String(student.admissionNo || "") === String(id)
        );
      });

      if (matchedStudent) return displayName(matchedStudent);

      return looksLikeFirestoreId(id) ? "Student record" : String(id || "Student");
    });
  };

  const renderLinkedStudentNames = (ids, source = null) => {
    const names = resolveLinkedStudentNames(source || ids, ids);
    if (!names.length) return "—";
    return names.join(", ");
  };

  if (!currentSchoolId) {
    return (
      <div style={styles.page}>
        <MotionCard style={styles.panel} hover={false}>
          <div style={styles.panelEyebrow}>Parent onboarding</div>
          <h3 style={styles.panelTitle}>Loading parent data...</h3>
          <div style={styles.panelText}>
            Waiting for the correct school ID before loading parent records.
          </div>
        </MotionCard>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <MotionCard style={styles.hero} hover={false}>
        <div style={styles.heroLeft}>
          <div style={styles.heroEyebrow}>PresentPlus Admin</div>
          <h2 style={styles.heroTitle}>Parent Onboarding Command Centre</h2>
          <p style={styles.heroText}>
            Import parent contacts, stage them safely, manually link children,
            and only send invites when every record is verified.
          </p>
        </div>

        <div style={styles.heroBadge}>
          <div style={styles.heroBadgeLabel}>Ready drafts</div>
          <div style={styles.heroBadgeValue}>
            {formatCount(summary.readyDrafts)}
          </div>
        </div>
      </MotionCard>

      {error ? <div style={styles.errorBox}>{error}</div> : null}
      {success ? <div style={styles.successBox}>{success}</div> : null}

      <section style={styles.statGrid}>
        <FadeUpStagger>
          <StatCard label="Parent Drafts" value={summary.drafts} tone="warning" />
          <StatCard label="Needs Link" value={summary.needsLinkDrafts} tone="danger" />
          <StatCard label="Ready Drafts" value={summary.readyDrafts} tone="success" />
          <StatCard label="Total Parents" value={summary.total} tone="default" />
          <StatCard label="Accepted" value={summary.accepted} tone="blue" />
        </FadeUpStagger>
      </section>

      <MotionCard style={styles.engagementPanel} hover={false}>
        <div style={styles.panelEyebrow}>Parent engagement</div>
        <h3 style={styles.engagementTitle}>Invite-to-active journey</h3>
        <p style={styles.engagementText}>
          See who received an invite, completed setup, logged in, and is still using PresentPlus.
        </p>

        <div style={styles.engagementGrid}>
          <StatCard label="Invited" value={summary.invited} tone="warning" />
          <StatCard label="Joined" value={summary.joined} tone="blue" />
          <StatCard label="First Login" value={summary.firstLogin} tone="success" />
          <StatCard label="Active (30 days)" value={summary.engaged} tone="success" />
          <StatCard label="Inactive" value={summary.inactive} tone="danger" />
        </div>
      </MotionCard>

      <MotionCard style={styles.panel}>
        <div style={styles.panelHeadRowSplit}>
          <div>
            <div style={styles.panelEyebrow}>CSV onboarding</div>
            <h3 style={styles.panelTitle}>Upload parent contacts</h3>
            <div style={styles.panelText}>
              Use a simple CSV with <strong>Parent Name, Parent Email</strong>.
              This creates drafts only — no child is linked automatically.
            </div>
          </div>

          <label style={styles.uploadButton}>
            Choose CSV
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={(e) => handleCSVUpload(e.target.files?.[0])}
            />
          </label>
        </div>

        <div style={styles.csvSummaryBox}>
          CSV format: <strong>Parent Name, Parent Email</strong>
        </div>
      </MotionCard>
            <MotionCard style={styles.panel}>
        <div style={styles.panelHeadRowSplit}>
          <div>
            <div style={styles.panelEyebrow}>Draft staging</div>
            <h3 style={styles.panelTitle}>Parent drafts</h3>
            <div style={styles.panelText}>
              Link children manually before sending invites. No automatic linking.
            </div>
          </div>

          <div style={styles.draftActionsRight}>
            <div style={styles.filterWrap}>
              {["all", "ready", "needsLink"].map((item) => {
                const active = draftFilter === item;

                return (
                  <MotionButton
                    key={item}
                    onClick={() => setDraftFilter(item)}
                    style={{
                      ...styles.filterChip,
                      ...(active ? styles.filterChipActive : {}),
                    }}
                  >
                    {item === "needsLink" ? "Needs Link" : item}
                  </MotionButton>
                );
              })}
            </div>

            <MotionButton
              onClick={handleBulkSendInvites}
              style={styles.primaryButton}
              disabled={saving || !readyDrafts.length}
            >
              Send Ready Invites ({readyDrafts.length})
            </MotionButton>
          </div>
        </div>

        {draftRows.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Parent</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Linked Children</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {draftRows.map((draft) => {
                  const isReady = draft.isReady;
                  const isBusy = busyId === `draft-${draft.id}`;

                  return (
                    <tr key={draft.id}>
                      <td style={styles.td}>
                        <div style={styles.personCell}>
                          <div style={styles.personAvatar}>
                            {String(draft.name || "P")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div>
                            <div style={styles.personName}>
                              {draft.name || "—"}
                            </div>
                            <div style={styles.personSub}>Draft record</div>
                          </div>
                        </div>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.emailText}>
                          {draft.email || "—"}
                        </div>
                      </td>

                      <td style={styles.td}>
                        {isReady ? (
                          <div style={styles.linkedText}>
                            {renderLinkedStudentNames(draft.linkedIds, draft)}
                          </div>
                        ) : (
                          <div style={styles.warningText}>
                            ⚠ Not linked to any child
                          </div>
                        )}
                      </td>

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...getStatusTone(
                              isReady ? "ready" : "needs link"
                            ),
                          }}
                        >
                          {isReady ? "Ready" : "Needs Link"}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.actionsWrap}>
                          <MotionButton
                            onClick={() =>
                              openManageChildren({
                                ...draft,
                                __type: "draft",
                              })
                            }
                            style={styles.manageBtn}
                          >
                            Manage Children
                          </MotionButton>

                          <MotionButton
                            onClick={() => handleDeleteDraft(draft)}
                            style={styles.deleteBtn}
                            disabled={isBusy}
                          >
                            Delete
                          </MotionButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>
            No drafts available. Upload a CSV to begin.
          </div>
        )}
      </MotionCard>
            <MotionCard style={styles.panel}>
        <div style={styles.panelHead}>
          <div>
            <div style={styles.panelEyebrow}>Manual onboarding</div>
            <h3 style={styles.panelTitle}>Invite single parent</h3>
            <div style={styles.panelText}>
              Add a parent manually, link children, and send invite instantly.
            </div>
          </div>
        </div>

 <form onSubmit={handleInviteParent} style={styles.formGrid}>
  <div style={styles.fieldGrid}>
    <input
      id="parent-name"
      name="parentName"
      autoComplete="name"
      style={styles.input}
      placeholder="Parent name"
      value={form.name}
      onChange={(e) =>
        setForm((p) => ({ ...p, name: e.target.value }))
      }
    />

    <input
      id="parent-email"
      name="parentEmail"
      type="email"
      autoComplete="email"
      style={styles.input}
      placeholder="Parent email"
      value={form.email}
      onChange={(e) =>
        setForm((p) => ({ ...p, email: e.target.value }))
      }
    />

    <select
      id="parent-relationship"
      name="parentRelationship"
      style={styles.input}
      value={form.relationship}
      onChange={(e) =>
        setForm((p) => ({
          ...p,
          relationship: e.target.value,
          relationshipOther:
            e.target.value === "other" ? p.relationshipOther : "",
        }))
      }
      aria-label="Parent relationship"
    >
      {RELATIONSHIP_OPTIONS.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>

    {form.relationship === "other" ? (
      <input
        id="parent-relationship-other"
        name="parentRelationshipOther"
        style={styles.input}
        placeholder="Enter relationship"
        value={form.relationshipOther}
        onChange={(e) =>
          setForm((p) => ({ ...p, relationshipOther: e.target.value }))
        }
      />
    ) : null}
  </div>

  <div style={styles.pickerWrap}>
    <div style={styles.pickerLabel}>Link students</div>

    <div style={styles.searchRow}>
      <input
        id="student-search"
        name="studentSearch"
        autoComplete="off"
        style={styles.input}
        placeholder="Search student"
        value={studentSearch}
        onChange={(e) => {
          setStudentSearch(e.target.value);
          setDropdownOpen(true);
        }}
        onFocus={() => setDropdownOpen(true)}
      />

              <MotionButton
                onClick={() => setDropdownOpen((v) => !v)}
                style={styles.secondaryAction}
              >
                Select Student
              </MotionButton>
            </div>

            {dropdownOpen && (
              <div style={styles.dropdownMenu}>
                {filteredStudents.length ? (
                  filteredStudents.slice(0, 12).map((s) => (
                    <MotionButton
                      key={s.id}
                      onClick={() => handleSelectStudent(s.id)}
                      style={styles.dropdownItem}
                    >
<div style={styles.dropdownItemMain}>
  {displayName(s)}
</div>
                    </MotionButton>
                  ))
                ) : (
                  <div style={styles.dropdownEmpty}>
                    No students found.
                  </div>
                )}
              </div>
            )}

            {!!form.linkedStudentIds.length && (
              <div style={styles.selectedWrap}>
                {form.linkedStudentIds.map((id) => (
                  <MotionCard key={id} style={styles.selectedChip}>
                    <span>{studentNameMap[id] || "Unnamed student"}</span>
                    <button
                      type="button"
                      style={styles.removeChipBtn}
                      onClick={() => handleRemoveStudent(id)}
                    >
                      <span style={{ transform: "translateY(-1px)" }}>×</span>
                    </button>
                  </MotionCard>
                ))}
              </div>
            )}
          </div>

          <div style={styles.formActions}>
            <MotionButton
              style={styles.primaryButton}
              type="submit"
              disabled={saving}
            >
              {saving ? "Creating..." : "Invite Parent"}
            </MotionButton>
          </div>
        </form>
      </MotionCard>
            <MotionCard style={styles.panel}>
        <div style={styles.panelHeadRow}>
          <div>
            <div style={styles.panelEyebrow}>Parent directory</div>
            <h3 style={styles.panelTitle}>Family access records</h3>
            <div style={styles.panelText}>
              Search, filter, approve, reject, resend, revoke, and manage linked
              children.
            </div>
          </div>
        </div>

        <div style={styles.controlsRow}>
          <div style={styles.searchWrapWide}>
            <input
              style={styles.searchInput}
              placeholder="Search parent, email, linked student, or status"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
          </div>

          <div style={styles.filterWrap}>
            {[
              "all",
              "not_joined",
              "invited",
              "joined",
              "active",
              "inactive",
              "pending",
              "accepted",
              "rejected",
              "revoked",
              "expired",
            ].map((item) => {
              const activeChip = statusFilter === item;

              return (
                <MotionButton
                  key={item}
                  onClick={() => setStatusFilter(item)}
                  style={{
                    ...styles.filterChip,
                    ...(activeChip ? styles.filterChipActive : {}),
                  }}
                >
                  {engagementLabel(item) === "Not joined" && item !== "not_joined"
                    ? item.charAt(0).toUpperCase() + item.slice(1)
                    : engagementLabel(item)}
                </MotionButton>
              );
            })}
          </div>
        </div>

        {parentRows.length ? (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Parent</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Linked Students</th>
                  <th style={styles.th}>Account</th>
                  <th style={styles.th}>Engagement</th>
                  <th style={styles.th}>First login</th>
                  <th style={styles.th}>Last active</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {parentRows.map((item) => {
                  const status = item.displayStatus;
                  const isBusy = busyId === item.id;

                  return (
                    <tr key={item.id}>
                      <td style={styles.td}>
                        <div style={styles.personCell}>
                          <div style={styles.personAvatar}>
                            {String(item.name || "P")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>

                          <div>
                            <div style={styles.personName}>
                              {item.name || "—"}
                            </div>

                            {relationshipSummary(item, studentNameMap).length ? (
                              <div style={styles.relationshipStack}>
                                {relationshipSummary(item, studentNameMap).map(
                                  (entry) => (
                                    <div
                                      key={`${item.id}-${entry.studentId}`}
                                      style={styles.relationshipLine}
                                    >
                                      {entry.relationshipLabel} of {entry.studentName}
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <div style={styles.relationshipLine}>
                                {item.relationshipLabel ||
                                  getRelationshipLabel(item.relationship)}
                              </div>
                            )}

                            <div style={styles.personSub}>Parent account</div>
                          </div>
                        </div>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.emailText}>{item.email || "—"}</div>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.linkedText}>
                          {renderLinkedStudentNames(item.linkedStudentIds, item)}
                        </div>
                      </td>

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...getStatusTone(status),
                          }}
                        >
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <span
                          style={{
                            ...styles.statusBadge,
                            ...getEngagementTone(item.engagementStatus),
                          }}
                        >
                          {engagementLabel(item.engagementStatus)}
                        </span>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.activityDate}>
                          {formatActivityDate(item.firstLoginAt)}
                        </div>
                      </td>

                      <td style={styles.td}>
                        <div style={styles.activityDate}>
                          {formatActivityDate(item.lastActiveAt)}
                        </div>
                        {item.loginCount > 0 ? (
                          <div style={styles.activityMeta}>
                            {item.loginCount} login{item.loginCount === 1 ? "" : "s"}
                          </div>
                        ) : null}
                      </td>

                      <td style={styles.td}>
                        <div style={styles.actionsWrap}>
                          <MotionButton
                            onClick={() => openManageChildren(item)}
                            style={styles.manageBtn}
                            disabled={isBusy}
                          >
                            Manage Children
                          </MotionButton>

                          {status !== "active" ? (
                            <MotionButton
                              onClick={() => handleApprove(item)}
                              style={styles.approveBtn}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working..." : "Approve"}
                            </MotionButton>
                          ) : null}

                          {!["rejected", "revoked", "expired"].includes(status) ? (
                            <MotionButton
                              onClick={() => handleReject(item)}
                              style={styles.rejectBtn}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working..." : "Reject"}
                            </MotionButton>
                          ) : null}

                          {["pending", "rejected", "revoked", "expired"].includes(
                            status
                          ) ? (
                            <MotionButton
                              onClick={() => handleResendInvite(item)}
                              style={styles.resendBtn}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working..." : "Resend"}
                            </MotionButton>
                          ) : null}

                          {["pending", "rejected"].includes(status) ? (
                            <MotionButton
                              onClick={() => handleRevokeInvite(item)}
                              style={styles.revokeBtn}
                              disabled={isBusy}
                            >
                              {isBusy ? "Working..." : "Revoke"}
                            </MotionButton>
                          ) : null}

                          <MotionButton
                            onClick={() => handleDelete(item)}
                            style={styles.deleteBtn}
                            disabled={isBusy}
                          >
                            {isBusy ? "Working..." : "Remove"}
                          </MotionButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={styles.emptyState}>No parent records match this view yet.</div>
        )}
      </MotionCard>
            {editingParent ? (
        <div style={styles.modalOverlay} onClick={closeManageChildren}>
          <MotionCard style={styles.modalCard} hover={false}>
            <div onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <div>
                  <div style={styles.panelEyebrow}>Parent linking</div>
                  <h3 style={styles.modalTitle}>Manage children</h3>
                  <div style={styles.panelText}>
                    Update linked students for{" "}
                    {editingParent.name || "this parent"}.
                  </div>
                </div>

                <MotionButton
                  onClick={closeManageChildren}
                  style={styles.modalCloseBtn}
                >
                  <span style={{ transform: "translateY(-1px)" }}>×</span>
                </MotionButton>
              </div>

              <input
                style={styles.input}
                placeholder="Search student"
                value={editSearch}
                onChange={(e) => setEditSearch(e.target.value)}
              />

              <div style={styles.modalList}>
                {editableStudents.length ? (
                  editableStudents.map((student) => {
                    const selected = editStudentIds.includes(student.id);

                    return (
                      <MotionButton
                        key={student.id}
                        onClick={() => toggleEditStudent(student.id)}
                        style={{
                          ...styles.modalStudentRow,
                          ...(selected
                            ? styles.modalStudentRowActive
                            : {}),
                        }}
                      >
                        <div style={styles.modalStudentMain}>
                          {displayName(student)}
                        </div>

                        <div
                          style={{
                            ...styles.modalStudentSub,
                            ...(selected
                              ? { color: "rgba(255,255,255,0.9)" }
                              : {}),
                          }}
                        >
                          {getStudentMeta(student, classMap)}
                        </div>
                      </MotionButton>
                    );
                  })
                ) : (
                  <div style={styles.dropdownEmpty}>
                    No students found.
                  </div>
                )}
              </div>

              {!!editStudentIds.length && (
                <div style={styles.relationshipEditorList}>
                  {editStudentIds.map((id) => (
                    <MotionCard key={id} style={styles.relationshipEditorCard}>
                      <div style={styles.relationshipEditorCopy}>
                        <strong>{studentNameMap[id] || "Student record"}</strong>
                        <span>Choose this parent’s relationship to this child.</span>
                      </div>

                      <select
                        value={
                          editRelationships[id] ||
                          relationshipForStudent(editingParent, id).relationship ||
                          "guardian"
                        }
                        onChange={(event) =>
                          changeEditRelationship(id, event.target.value)
                        }
                        style={styles.relationshipSelect}
                      >
                        {RELATIONSHIP_OPTIONS.filter(
                          (option) => option.value !== "other"
                        ).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>

                      <button
                        type="button"
                        style={styles.removeRelationshipBtn}
                        onClick={() => toggleEditStudent(id)}
                        aria-label={`Remove ${studentNameMap[id] || "student"}`}
                      >
                        ×
                      </button>
                    </MotionCard>
                  ))}
                </div>
              )}

              <div style={styles.modalActions}>
                <MotionButton
                  onClick={closeManageChildren}
                  style={styles.secondaryButton}
                  disabled={saving}
                >
                  Cancel
                </MotionButton>

                <MotionButton
                  onClick={saveLinkedChildren}
                  style={styles.primaryButton}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </MotionButton>
              </div>
            </div>
          </MotionCard>
        </div>
      ) : null}
    </div>
  );
}
function StatCard({ label, value, tone = "default" }) {
  const color =
    tone === "success" ? "#027a48" :
    tone === "danger" ? "#b42318" :
    tone === "warning" ? "#b54708" :
    tone === "blue" ? "#175cd3" :
    "#112042";
  return (
    <MotionCard style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color }}>{formatCount(value)}</div>
    </MotionCard>
  );
}

const styles = {
  page: { display: "flex", flexDirection: "column", gap: 22 },

  hero: {
    borderRadius: 28,
    padding: "30px 28px",
    background: "var(--pp-brand-gradient)",
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    gap: 20,
  },
  heroLeft: { minWidth: 0 },
  heroEyebrow: { fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 800 },
  heroTitle: { margin: 0, fontSize: 38, fontWeight: 900, letterSpacing: "-0.04em" },
  heroText: { marginTop: 10, fontSize: 15, lineHeight: 1.6, maxWidth: 760 },
  heroBadge: { minWidth: 180, borderRadius: 22, padding: 18, background: "rgba(255,255,255,0.16)" },
  heroBadgeLabel: { fontSize: 12, fontWeight: 800, textTransform: "uppercase" },
  heroBadgeValue: { fontSize: 34, fontWeight: 900 },

  statGrid: { display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 14 },
  statCard: { borderRadius: 22, padding: 20, background: "#fff", border: "1px solid #e9eefb" },
  statLabel: { fontSize: 12, fontWeight: 800, color: "#667085", textTransform: "uppercase" },
  statValue: { marginTop: 10, fontSize: 34, fontWeight: 900 },

  panel: { background: "#fff", borderRadius: 28, border: "1px solid #e9eefb", padding: 24 },
  panelHead: { marginBottom: 18 },
  panelHeadRow: { marginBottom: 18 },
  panelHeadRowSplit: { display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 18 },
  panelEyebrow: { fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#667085", fontWeight: 800 },
  panelTitle: { margin: 0, fontSize: 28, fontWeight: 900, color: "#112042" },
  panelText: { marginTop: 8, color: "#667085", lineHeight: 1.6 },
  engagementPanel: {
    padding: 22,
    borderRadius: 24,
    background: "linear-gradient(180deg, #ffffff 0%, #f7faff 100%)",
    border: "1px solid #e3ebf8",
  },
  engagementTitle: { margin: "4px 0 0", fontSize: 24, fontWeight: 900, color: "#112042" },
  engagementText: { margin: "8px 0 18px", color: "#667085", lineHeight: 1.55 },
  engagementGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 14,
  },

  input: { width: "100%", padding: "14px 16px", borderRadius: 16, border: "1px solid #d8e1f2", boxSizing: "border-box" },
  fieldGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 },
  formGrid: { display: "grid", gap: 16 },
  pickerWrap: { padding: 16, borderRadius: 18, border: "1px solid #dbe7ff", background: "#f8fbff" },
  pickerLabel: { fontSize: 13, fontWeight: 800, marginBottom: 12 },
  searchRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 10 },

  primaryButton: { padding: "14px 18px", borderRadius: 16, border: "none", background: "#2563eb", color: "#fff", fontWeight: 800 },
  secondaryAction: { padding: "14px 16px", borderRadius: 16, border: "1px solid #dde5f5", background: "#fff", color: "#334155", fontWeight: 800 },
  secondaryButton: { padding: "14px 18px", borderRadius: 16, border: "1px solid #dde5f5", background: "#ffffff", color: "#334155", fontWeight: 800 },

  tableWrap: { overflowX: "auto", borderRadius: 20, border: "1px solid #edf2fb" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: 14, background: "#f9fbff", borderBottom: "1px solid #edf2fb", fontSize: 12, fontWeight: 800 },
  td: { padding: 14, borderBottom: "1px solid #f1f5fb", fontSize: 14 },

  personCell: { display: "flex", alignItems: "center", gap: 12 },
  personAvatar: { width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", background: "#4f63ff", color: "#fff", fontWeight: 900 },
  personName: { fontWeight: 800 },
  relationshipStack: {
    display: "grid",
    gap: 3,
    marginTop: 4,
  },
  relationshipLine: {
    fontSize: 12,
    color: "#2563eb",
    fontWeight: 850,
    lineHeight: 1.35,
  },
  personSub: { fontSize: 13, color: "#667085" },
  emailText: { color: "#334155" },
  linkedText: { color: "#475569" },
  warningText: { color: "#b54708", fontWeight: 800 },
  statusBadge: { padding: "8px 12px", borderRadius: 999, fontSize: 12, fontWeight: 800 },
  activityDate: { color: "#344054", fontSize: 13, fontWeight: 800, whiteSpace: "nowrap" },
  activityMeta: { marginTop: 4, color: "#667085", fontSize: 11, fontWeight: 700 },

  actionsWrap: { display: "flex", gap: 8, flexWrap: "wrap" },
  manageBtn: { padding: "9px 12px", borderRadius: 12, border: "1px solid #cfe0ff", background: "#eef4ff", color: "#1d4ed8", fontWeight: 700 },
  approveBtn: { padding: "9px 12px", borderRadius: 12, border: "none", background: "#4f63ff", color: "#fff", fontWeight: 700 },
  rejectBtn: { padding: "9px 12px", borderRadius: 12, border: "none", background: "#fff1f3", color: "#b42318", fontWeight: 700 },
  resendBtn: { padding: "9px 12px", borderRadius: 12, border: "none", background: "#f5f8ff", color: "#2845d9", fontWeight: 700 },
  revokeBtn: { padding: "9px 12px", borderRadius: 12, border: "none", background: "#fff7ec", color: "#b54708", fontWeight: 700 },
  deleteBtn: {
    padding: "9px 12px",
    borderRadius: 12,
    border: "1px solid #e5eaf5",
    background: "#ffffff",
    color: "#b42318",
    fontWeight: 800,
    opacity: 1,
    cursor: "pointer",
  },

  filterWrap: { display: "flex", gap: 10, flexWrap: "wrap" },
  filterChip: { padding: "10px 14px", borderRadius: 999, border: "1px solid #e2e8f5", background: "#fff", color: "#334155", fontWeight: 800 },
  filterChipActive: { background: "#2563eb", color: "#fff", border: "1px solid #2563eb" },

  controlsRow: { display: "grid", gridTemplateColumns: "1fr auto", gap: 14, marginBottom: 18 },
  searchWrapWide: { minWidth: 0 },
  searchInput: { width: "100%", padding: "14px 16px", borderRadius: 18, border: "1px solid #d8e1f2", boxSizing: "border-box" },

  dropdownMenu: { marginTop: 12, borderRadius: 18, border: "1px solid #e5ebf7", background: "#fff", overflow: "hidden" },
  dropdownItem: { width: "100%", textAlign: "left", padding: 14, border: "none", background: "#ffffff" },
  dropdownItemMain: { fontSize: 15, fontWeight: 800, color: "#14213d", lineHeight: 1.3 },
  dropdownItemSub: { fontSize: 13, color: "#334155", fontWeight: 700 },
  dropdownEmpty: { padding: 16, color: "#667085" },

  selectedWrap: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 14 },
selectedChip: {
  padding: "10px 14px",
  borderRadius: 999,
  background: "#eef4ff",
  color: "#14213d",
  display: "flex",
  gap: 8,
  alignItems: "center",
  boxShadow: "0 10px 24px rgba(37,99,235,0.08)",
  fontWeight: 800,
},
  removeChipBtn: {
    width: 30,
    height: 30,
    borderRadius: 12,
    border: "none",
    background: "rgba(255,255,255,0.9)",
    color: "#334155",
    cursor: "pointer",
    fontWeight: 900,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    padding: 0,
    flexShrink: 0,
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 1000,
    background: "rgba(15, 23, 42, 0.42)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },

  modalCard: {
    width: "100%",
    maxWidth: 860,
    maxHeight: "92vh",
    overflow: "auto",
    borderRadius: 28,
    background: "rgba(255,255,255,0.84)",
    border: "1px solid rgba(255,255,255,0.78)",
    boxShadow: "0 30px 90px rgba(15,23,42,0.22)",
    padding: 28,
  },

  modalHeader: { display: "flex", justifyContent: "space-between", marginBottom: 16 },
  modalTitle: { margin: 0, fontSize: 28, fontWeight: 900, color: "#112042" },
  modalCloseBtn: {
    width: 46,
    height: 46,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.7)",
    background: "#ffffff",
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    padding: 0,
    flexShrink: 0,
  },

  modalList: {
    display: "grid",
    gap: 10,
    marginTop: 16,
    maxHeight: 260,
    overflowY: "auto",
    padding: 10,
    borderRadius: 24,
    background: "rgba(255,255,255,0.72)",
    border: "1px solid rgba(226,232,240,0.75)",
  },
  modalStudentRow: {
    width: "100%",
    textAlign: "left",
    borderRadius: 18,
    border: "1px solid rgba(226,232,240,0.85)",
    background: "#ffffff",
    padding: "14px 16px",
    cursor: "pointer",
    boxShadow: "0 10px 24px rgba(15,23,42,0.04)",
  },
  modalStudentRowActive: {
    background: "#ffffff",
    border: "1px solid #bcd0ff",
    color: "#14213d",
    boxShadow: "0 12px 26px rgba(79,99,255,0.12)",
  },
  modalStudentMain: { fontSize: 15, fontWeight: 900, lineHeight: 1.35, color: "#14213d" },
  modalStudentSub: {
  marginTop: 5,
  fontSize: 13,
  color: "#14213d",
  lineHeight: 1.4,
  fontWeight: 800,
},

  relationshipEditorList: {
    display: "grid",
    gap: 12,
    marginTop: 16,
  },
  relationshipEditorCard: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(150px, 210px) 42px",
    gap: 12,
    alignItems: "center",
    padding: 14,
    borderRadius: 20,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    boxShadow: "0 10px 26px rgba(37,99,235,0.07)",
  },
  relationshipEditorCopy: {
    display: "grid",
    gap: 4,
    color: "#14213d",
  },
  relationshipSelect: {
    width: "100%",
    minHeight: 44,
    padding: "0 12px",
    borderRadius: 14,
    border: "1px solid #cbd8ee",
    background: "#f8fbff",
    color: "#14213d",
    fontWeight: 800,
    outline: "none",
  },
  removeRelationshipBtn: {
    width: 40,
    height: 40,
    borderRadius: 13,
    border: "1px solid #fecdca",
    background: "#fff1f2",
    color: "#b42318",
    fontSize: 20,
    fontWeight: 900,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
  },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 },

  formActions: { display: "flex" },
  draftActionsRight: { display: "grid", gap: 10, justifyItems: "end" },
  uploadButton: { padding: "14px 18px", borderRadius: 16, border: "1px solid #dde5f5", background: "#fff", fontWeight: 800, cursor: "pointer" },
  csvSummaryBox: { padding: 14, borderRadius: 16, background: "#f8fbff", border: "1px solid #dbe7ff" },
  emptyState: { padding: 18, borderRadius: 18, background: "#fbfcff", border: "1px solid #edf2fb", color: "#667085" },
  errorBox: { padding: 14, borderRadius: 16, background: "#fff1f2", color: "#9f1239", fontWeight: 700 },
  successBox: { padding: 14, borderRadius: 16, background: "#ecfdf5", color: "#166534", fontWeight: 700 },
};